/**
 * @module components/AssetPackageStatus
 * @description Status badge display for asset packages showing provider, status, and expiry info.
 * Displays provider name, package status (pending/active/expired), and time until expiry.
 */

import React, { useMemo } from 'react';
import { FaCalendarTimes } from 'react-icons/fa';
import { Tooltip } from 'src/components/Tooltip';
import { Assets } from 'src/types/assets';
import { Sanity } from 'src/types/sanity';
import { formatRelativeDate, getExpiryStatus, getExpiryTone, getPackageStatus, getStatusTone, getTimestamp, randSelector } from 'src/utils';
import { SanityBadge } from '../SanityBadge';
import * as Styles from './styles';
import { AssetPackageStatusProps } from './types';

/**
 * @component AssetPackageStatus
 * @summary Displays package status with provider badge, status badge, and expiry countdown.
 * Shows different status tones (positive/caution/critical) based on package state and time remaining.
 * @type {React.FC<AssetPackageStatusProps>}
 * @param {AssetPackageStatusProps} props - Component props from types.ts
 * @returns {React.ReactElement} Badge group with provider, status, and expiry information
 */
export const AssetPackageStatus: React.FC<AssetPackageStatusProps> = ({ assetPackage, className, transferStatus }) => {
  const effectiveTransferStatus = transferStatus ?? assetPackage.transferStatus;
  const expiryStatus = getExpiryStatus(assetPackage);
  const packageStatus = getPackageStatus(effectiveTransferStatus!, expiryStatus);
  const statusTone = getStatusTone({ ...assetPackage, transferStatus: effectiveTransferStatus });

  const expiryDate = useMemo(() => formatRelativeDate({
    date: assetPackage.expiry || assetPackage.linkExpiry!,
    showTimestamp: false,
  }), [assetPackage]);

  const expiryTimestamp = useMemo(() => getTimestamp(assetPackage.expiry || assetPackage.linkExpiry!), [assetPackage]);

  const expiryId = randSelector('badge-item-expiry');

  // console.warn('** package/status', {
  //   statusTone,
  //   packageStatus,
  //   expiryStatus,
  //   expiryDate,
  //   expiryTimestamp,
  //   expiryId,
  // });

  return (
    <Styles.BadgeGroup className={className}>
      <Styles.BadgeItem>
        <SanityBadge
          tone={Sanity.Types.SanityTone.PRIMARY}
          outline
        >
          {Assets.Types.ProviderNames[assetPackage.assetProvider]}
        </SanityBadge>
      </Styles.BadgeItem>
      <Styles.BadgeItem>
        <SanityBadge
          tone={statusTone}
          outline
          icon={(packageStatus === Assets.Types.PackageStatus.ACTIVE && expiryStatus.warning)
            ? (
              Assets.Data.ExpiryStatusIcons[Assets.Types.ExpiryStatus.WARNING]
            ) : (
              Assets.Data.PackageStatusIcons[packageStatus]
            )}
          >
          {packageStatus !== Assets.Types.PackageStatus.ACTIVE
            ? Assets.Data.PackageStatusLabels[packageStatus]
            : expiryStatus.statusText
          }
        </SanityBadge>
      </Styles.BadgeItem>
      { !expiryStatus.isAnyExpired && (
        <Styles.BadgeItem>
          <SanityBadge
            tone={getExpiryTone(assetPackage)}
            outline
            icon={<FaCalendarTimes />}
            data-tooltip-id={expiryId}
            data-tooltip-content={`Expires: ${expiryTimestamp}`}
          >
            {expiryDate}
          </SanityBadge>
          <Tooltip key={expiryId} id={expiryId} />
        </Styles.BadgeItem>
      )}
    </Styles.BadgeGroup>
  );
};
